import React, { useEffect, useRef, useState } from 'react';
import { FiDownload, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { downloadElementPdf } from '../utils/downloadElementPdf';
import DOMPurify from 'dompurify';
import { graphqlRequest } from '../utils/graphqlRequest';
import { formatAUD } from '../utils/formatCurrency';
import { mergeTermRows } from './ProfilePages/components/quoteCatalog';
import { proposalTermSections } from './Proposals/utils/proposalUtils';
import '../styles/Contract.css';

const VERSION_FIELDS = `
  id
  contractNumber
  versionNumber
  quotationNumber
  title
  clientName
  clientEmail
  companyName
  siteAddress
  contactPerson
  billingAddress
  phoneNumber
  customerType
  serviceType
  frequency
  siteSize
  staffRequired
  serviceCost
  gstAmount
  additionalCharges
  discount
  value
  paymentReceived
  paymentTerms
  status
  scopeOfWork
  services
  terms
  notes
  contractDate
  startDate
  endDate
  validUntil
  sentAt
  signedAt
  signedName
  signedPosition
  signatureImage
  signatureText
  rejectReason
`;

const FIND_CONTRACT_VERSIONS = `
  query FindClientContractVersions($contractNumber: String!) {
    findClientContractVersions(contractNumber: $contractNumber) {
      ${VERSION_FIELDS}
    }
  }
`;

const STATUS_FROM_API = {
  DRAFT: 'Draft',
  PENDING_SIGNATURE: 'Pending Signature',
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  TERMINATED: 'Terminated',
  CHANGE_REQUESTED: 'Change Requested',
  DECLINED: 'Declined',
};

const STATUS_CLASS = {
  Active: 'con-status--active',
  'Pending Signature': 'con-status--pending',
  Draft: 'con-status--draft',
  Completed: 'con-status--completed',
  Terminated: 'con-status--terminated',
  'Change Requested': 'con-status--changes',
  Declined: 'con-status--terminated',
};

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function display(value) {
  const text = String(value ?? '').trim();
  return text || '—';
}

function contractTermSections(row) {
  const stored = (Array.isArray(row?.services) ? row.services : []).flatMap((line) =>
    Array.isArray(line?.sourceTerms) ? line.sourceTerms : [],
  );
  if (stored.length) {
    const list = mergeTermRows(stored, []).filter((item) => item && item.selected !== false);
    const text = (item) => String(item?.text || '').trim();
    const sections = [
      {
        title: 'Service Terms',
        kind: 'pairs',
        rows: list
          .filter((item) => item.section === 'serviceTerms')
          .map((item) => ({
            title: String(item.title || '').trim(),
            body: String(item.body || '').trim(),
          }))
          .filter((item) => item.title || item.body),
      },
      {
        title: 'Client Responsibilities',
        kind: 'lines',
        rows: list.filter((item) => item.section === 'responsibilities').map(text).filter(Boolean),
      },
      {
        title: 'Service Exclusions',
        kind: 'lines',
        rows: list.filter((item) => item.section === 'exclusions').map(text).filter(Boolean),
      },
      {
        title: 'Scope-Specific Notes',
        kind: 'lines',
        rows: list.filter((item) => item.section === 'notes').map(text).filter(Boolean),
      },
    ].filter((section) => section.rows.length);
    if (sections.length) return sections;
  }
  const plain = String(row?.terms || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '');
  return proposalTermSections(plain);
}

function TermSections({ row }) {
  const sections = contractTermSections(row);
  if (!sections.length) return <p className="con-detail-text">No terms recorded.</p>;
  return (
    <div className="con-view-terms">
      {sections.map((section) => (
        <article key={section.title}>
          <h5>{section.title}</h5>
          {section.kind === 'pairs' ? (
            section.rows.map((term, index) => (
              <div className="con-view-term-pair" key={`${section.title}-${index}`}>
                {term.title ? <strong>{term.title}</strong> : null}
                {term.body ? <p>{term.body}</p> : null}
              </div>
            ))
          ) : (
            <ul>
              {section.rows.map((line, index) => (
                <li key={`${section.title}-${index}`}>{line}</li>
              ))}
            </ul>
          )}
        </article>
      ))}
    </div>
  );
}

function displayRich(value) {
  const html = String(value ?? '').trim();
  if (!html) return '—';
  return (
    <div
      className="con-detail-text con-detail-html"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}

function clientResponse(row) {
  const api = String(row?.status || '').toUpperCase();
  if (api === 'ACTIVE' || api === 'COMPLETED') {
    return {
      tone: 'accepted',
      title: api === 'COMPLETED' ? 'Completed' : 'Signed',
      heading: 'Signed by',
      body: row.signedName || 'Client signed this revision',
      meta: row.signedAt ? `Signed at ${formatDateTime(row.signedAt)}` : '',
    };
  }
  if (api === 'CHANGE_REQUESTED') {
    return {
      tone: 'change',
      title: 'Change requested',
      heading: 'Client request',
      body: row.rejectReason || 'The client asked for changes.',
      meta: '',
    };
  }
  if (api === 'DECLINED') {
    return {
      tone: 'rejected',
      title: 'Declined',
      heading: 'Decline reason',
      body: row.rejectReason || 'The client declined this contract.',
      meta: '',
    };
  }
  if (api === 'PENDING_SIGNATURE') {
    return {
      tone: 'pending',
      title: 'Awaiting client',
      heading: '',
      body: 'Sent for signature. No client response yet.',
      meta: row.sentAt ? `Sent at ${formatDateTime(row.sentAt)}` : '',
    };
  }
  if (api === 'TERMINATED') {
    return {
      tone: 'rejected',
      title: 'Terminated',
      heading: '',
      body: row.rejectReason || 'This contract was terminated.',
      meta: '',
    };
  }
  return {
    tone: 'pending',
    title: 'Draft',
    heading: '',
    body: 'This revision has not been sent to the client.',
    meta: '',
  };
}

function Detail({ label, children, full }) {
  return (
    <div className={full ? 'con-detail con-detail--full' : 'con-detail'}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="con-detail-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function fallbackRow(contract, contractId) {
  if (!contract) return null;
  const apiStatus =
    Object.entries(STATUS_FROM_API).find(
      ([, label]) => label === contract.status,
    )?.[0] || 'DRAFT';
  return {
    id: contractId,
    contractNumber: contract.contractId,
    versionNumber: contract.versionNumber,
    quotationNumber: contract.quotationNumber,
    title: contract.title || contract.service,
    clientName: contract.client,
    clientEmail: contract.clientEmail,
    companyName: contract.companyName,
    siteAddress: contract.siteAddress,
    contactPerson: contract.contactPerson,
    billingAddress: contract.billingAddress,
    phoneNumber: contract.phoneNumber,
    customerType: contract.customerType,
    serviceType: contract.serviceType,
    frequency: contract.frequency,
    siteSize: contract.siteSize,
    staffRequired: contract.staffRequired,
    serviceCost: contract.serviceCost,
    gstAmount: contract.gstAmount,
    additionalCharges: contract.additionalCharges,
    discount: contract.discount,
    value: contract.total,
    paymentReceived: contract.received,
    paymentTerms: contract.paymentTerms,
    status: apiStatus,
    scopeOfWork: contract.scopeOfWork,
    terms: contract.terms,
    notes: contract.notes,
    contractDate: contract.contractDate,
    startDate: contract.startDate,
    endDate: contract.endDate,
    validUntil: contract.validUntil,
    sentAt: contract.sentAt,
    signedAt: contract.signedAt,
    signedName: contract.signedName,
    signedPosition: contract.signedPosition,
    signatureImage: contract.signatureImage,
    signatureText: contract.signatureText,
    rejectReason: contract.rejectReason,
  };
}

export default function ContractViewModal({
  contractId,
  contractNumber,
  contract,
  onClose,
  autoDownload = false,
  onPdf,
}) {
  const docRef = useRef(null);
  const autoDone = useRef(false);
  const [downloading, setDownloading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selectedId, setSelectedId] = useState(contractId || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setSelectedId(contractId || '');
  }, [contractId]);

  useEffect(() => {
    if (!contractNumber) {
      setVersions([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    graphqlRequest(FIND_CONTRACT_VERSIONS, { contractNumber })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.findClientContractVersions)
          ? res.findClientContractVersions
          : [];
        setVersions(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load contract details.');
        setVersions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contractNumber]);

  const selectedVersion =
    versions.find((row) => row.id === selectedId) || versions[0] || null;
  const row =
    selectedVersion || fallbackRow(contract, contractId) || { status: 'DRAFT' };
  const displayStatus = STATUS_FROM_API[row.status] || row.status || '';
  const displayVersion = Number(row.versionNumber) || 1;
  const response = clientResponse(row);

  const pdfName = `Contract-${row.contractNumber || contractNumber || 'contract'}-v${displayVersion}.pdf`;
  const downloadPdf = async (asBlob = false) => {
    setDownloading(true);
    try {
      const blob = await downloadElementPdf(docRef.current, pdfName, {
        output: asBlob === true ? 'blob' : 'save',
      });
      if (asBlob === true) await onPdf?.(blob, pdfName);
      return true;
    } catch (err) {
      console.error('Contract PDF failed:', err);
      toast.error('Could not download the contract PDF.');
      return false;
    } finally {
      setDownloading(false);
    }
  };

  // "PDF" from a document list: download as soon as the content is ready.
  useEffect(() => {
    if (!autoDownload || autoDone.current || loading || error) return;
    autoDone.current = true;
    void downloadPdf(Boolean(onPdf)).then(() => onClose());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownload, loading, error]);

  return (
    <div className="con-view-overlay" onClick={onClose} role="presentation">
      <div
        ref={docRef}
        className="con-view-modal con-view-modal--response"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="View contract details"
      >
        <header className="con-view-header">
          <div className="con-view-title">
            <div>
              <h2>Contract details</h2>
              <p>
                {row.contractNumber || contractNumber || 'Contract'}
                {` · v${displayVersion}`}
              </p>
            </div>
          </div>
          <div className="con-view-header-actions" data-pdf-ignore>
            <button
              type="button"
              className="con-view-pdf"
              onClick={() => downloadPdf()}
              disabled={loading || Boolean(error) || downloading}
            >
              <FiDownload size={15} /> {downloading ? 'Preparing…' : 'Download PDF'}
            </button>
            {versions.length > 1 ? (
              <label className="con-view-version">
                <span>Version</span>
                <select
                  value={selectedVersion?.id || selectedId}
                  onChange={(event) => setSelectedId(event.target.value)}
                >
                  {versions.map((item) => (
                    <option key={item.id} value={item.id}>
                      v{Number(item.versionNumber) || 1} ·{' '}
                      {STATUS_FROM_API[item.status] || item.status}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {displayStatus ? (
              <span className={`con-status ${STATUS_CLASS[displayStatus] || ''}`}>
                {displayStatus}
              </span>
            ) : null}
            <button
              type="button"
              className="con-view-close"
              onClick={onClose}
              aria-label="Close"
            >
              <FiX size={22} />
            </button>
          </div>
        </header>

        <div className="con-view-body con-view-body--response">
          {loading ? (
            <p className="con-view-message">Loading contract details…</p>
          ) : error ? (
            <p className="con-view-message con-view-message--error">{error}</p>
          ) : (
            <>
              <aside
                className={`con-response con-response--${response.tone}`}
                aria-label="Client response"
              >
                <p className="con-response-kicker">Client response</p>
                <strong className="con-response-title">{response.title}</strong>
                {response.meta ? (
                  <p className="con-response-meta">{response.meta}</p>
                ) : null}
                {response.heading ? (
                  <p className="con-response-heading">{response.heading}</p>
                ) : null}
                <p className="con-response-copy">{response.body}</p>
                {row.signedPosition ? (
                  <p className="con-response-meta">{row.signedPosition}</p>
                ) : null}
                {String(row.signatureImage || '').startsWith('data:image/') ? (
                  <img
                    className="con-response-sig"
                    src={row.signatureImage}
                    alt="Client signature"
                  />
                ) : row.signatureText ? (
                  <p className="con-response-sig-text">{row.signatureText}</p>
                ) : null}
              </aside>

              <Block title="Contract">
                <dl className="con-response-meta-grid">
                  <Detail label="Contract number">
                    {display(row.contractNumber || contractNumber)}
                  </Detail>
                  <Detail label="Revision">v{displayVersion}</Detail>
                  <Detail label="Quotation no.">
                    {display(row.quotationNumber)}
                  </Detail>
                  <Detail label="Title">{display(row.title)}</Detail>
                  <Detail label="Contract date">
                    {formatDate(row.contractDate)}
                  </Detail>
                  <Detail label="Start date">{formatDate(row.startDate)}</Detail>
                  <Detail label="End date">{formatDate(row.endDate)}</Detail>
                  <Detail label="Valid until">{formatDate(row.validUntil)}</Detail>
                </dl>
              </Block>

              <Block title="Client">
                <dl className="con-response-meta-grid">
                  <Detail label="Client name">{display(row.clientName)}</Detail>
                  <Detail label="Email">{display(row.clientEmail)}</Detail>
                  <Detail label="Company">{display(row.companyName)}</Detail>
                  <Detail label="Contact person">
                    {display(row.contactPerson)}
                  </Detail>
                  <Detail label="Phone">{display(row.phoneNumber)}</Detail>
                  <Detail label="Customer type">
                    {display(row.customerType)}
                  </Detail>
                  <Detail label="Site address" full>
                    {display(row.siteAddress)}
                  </Detail>
                  <Detail label="Billing address" full>
                    {display(row.billingAddress)}
                  </Detail>
                </dl>
              </Block>

              <Block title="Service">
                {Array.isArray(row.services) && row.services.length
                  ? row.services.map((line, index) => (
                      <div key={`${line.catalogServiceId || line.name}-${index}`} className="con-view-service">
                        <strong>{display(line.name)}</strong>
                        {(line.groups || []).map((group) => (
                          <div key={group.id || group.name}>
                            <p className="con-view-group">{group.name || 'Tasks'}</p>
                            <table className="con-view-tasks">
                              <thead>
                                <tr>
                                  <th>Task</th>
                                  <th>Client-facing scope</th>
                                  <th>Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(group.tasks || []).map((task) => (
                                  <tr key={task.id || task.title}>
                                    <td>{display(task.title)}</td>
                                    <td>{display(task.scope)}</td>
                                    <td>{formatAUD(Number(task.price) || 0)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    ))
                  : null}
                <dl className="con-response-meta-grid">
                  <Detail label="Service type">{display(row.serviceType)}</Detail>
                  <Detail label="Frequency">{display(row.frequency)}</Detail>
                  <Detail label="Site size">{display(row.siteSize)}</Detail>
                  <Detail label="Staff required">
                    {row.staffRequired != null && row.staffRequired !== ''
                      ? String(row.staffRequired)
                      : '—'}
                  </Detail>
                </dl>
              </Block>

              <Block title="Pricing">
                <dl className="con-response-meta-grid">
                  <Detail label="Service cost">
                    {formatAUD(Number(row.serviceCost) || 0)}
                  </Detail>
                  <Detail label="GST">
                    {formatAUD(Number(row.gstAmount) || 0)}
                  </Detail>
                  <Detail label="Additional charges">
                    {formatAUD(Number(row.additionalCharges) || 0)}
                  </Detail>
                  <Detail label="Discount">
                    {formatAUD(Number(row.discount) || 0)}
                  </Detail>
                  <Detail label="Total">
                    {formatAUD(Number(row.value) || 0)}
                  </Detail>
                  <Detail label="Payment received">
                    {formatAUD(Number(row.paymentReceived) || 0)}
                  </Detail>
                  <Detail label="Payment terms">
                    {display(row.paymentTerms)}
                  </Detail>
                </dl>
              </Block>

              <Block title="Scope of work">
                {displayRich(row.scopeOfWork)}
              </Block>
              <Block title="Terms">
                <TermSections row={row} />
              </Block>
              <Block title="Notes">
                {displayRich(row.notes)}
              </Block>
            </>
          )}
        </div>

        <footer className="con-view-footer" data-pdf-ignore>
          <button type="button" className="con-view-close-btn" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
